

/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useNavigate } from "react-router-dom";
import EventMetaSection from "./EventMetaSection";
import ParticipantsSection from "./ParticipantsSection";
import SinglesPartyAttendanceSummary from "./SinglesPartyAttendanceSummary";
import HostMoreActionsPanel, {
  usesHostPaymentRequest,
  isPaidEventHostTransferUnavailable,
} from "./HostMoreActionsPanel";
import JoinSection, { shouldUseParticipantRequestFlow } from "./JoinSection";
import api from "../../api/axios";
import HostRewardBlock from "./HostRewardBlock";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import TranslatableEventDescription from "./TranslatableEventDescription";
import ParticipantTicketPanel, {
  hasPaidParticipantReservation,
} from "./ParticipantTicketPanel";
import EventBuddyPanel from "./EventBuddyPanel";
import EventMetaIcon from "./EventMetaIcon";
import { BADGES } from "../../constants/badges";
import { isEventTicketPresentation } from "./eventPaymentSummary";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { formatPhysicalEventLocation } from "./eventLocation";
import { isConceptAwaitingDetails } from "./conceptPresentation";
import EventPromoGallerySection from "./EventPromoGallerySection";
import { getEventWeekdayAbbr } from "../../utils/eventDateLabels";
import { canJoinEventSession, isParticipantOnlyViewer } from "./eventRoleVisibility";
import { formatEventMoney } from "../../utils/eventCurrency";
import ValidationPendingNotice from "./ValidationPendingNotice";
import ChatNotificationBellIcon from "../menu/ChatNotificationBellIcon";

const EVENT_CHAT_BUTTON_ICON_SRC = "/assets/events/event_chat.svg";
const DEFAULT_EVENT_PICTURE_SRC = "/assets/event_pics/default.webp";
const pad2 = (value) => String(value).padStart(2, "0");

const getBadgeSrc = (label) => {
  const badge = BADGES.find((item) => item.id === label);
  return badge?.src || DEFAULT_EVENT_PICTURE_SRC;
};

const getEventCoverSrc = (ev) => String(ev?.hero_image_url || "").trim();

const getEventSummaryImageSrc = ({ eventIconSrc, badgeLabel }) => {
  if (eventIconSrc) return eventIconSrc;
  return getBadgeSrc(badgeLabel);
};

const isBusinessHostEvent = (ev) => {
  const hostFormatNumber = Number(ev?.host_format);
  return Number.isFinite(hostFormatNumber) && hostFormatNumber !== 0;
};

const parseEventDate = (ev, fallback) => {
  const raw = ev?.date_time || ev?.datetime || ev?.start_time || fallback;
  if (!raw) return null;
  const parsed = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
};

const formatEventCardDateTimeLabel = (ev, fallback, language) => {
  const date = parseEventDate(ev, fallback);
  if (!date) return String(fallback || "");
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())} ${pad2(date.getDate())}/${pad2(
    date.getMonth() + 1
  )} ${getEventWeekdayAbbr(date.getDay(), language)}`;
};

const DesktopEventSummaryCard = ({
  ev,
  imageSrc,
  dateLabel,
  isFavourite,
  onToggleFavourite,
  t,
}) => {
  const handleFavouriteClick = (event) => {
    event.stopPropagation();
    onToggleFavourite?.(event);
  };
  const isOnline = Number(ev?.event_format) === 0;
  const isPaid =
    Number(ev?.fee_type ?? 0) !== 0 &&
    (Number(ev?.payment_category_1 || 0) > 0 ||
      Number(ev?.payment_category_2 || 0) > 0 ||
      Number(ev?.payment_category_3 || 0) > 0 ||
      Number(ev?.payment_category_4 || 0) > 0 ||
      String(ev?.pricing_model || "").toLowerCase() !== "free");
  const summaryMetaIcons = [
    {
      id: "format",
      src: isOnline ? "/assets/events/online_icon.svg" : "/assets/events/offline_icon.svg",
      alt: isOnline
        ? t("event_card.icons.online_alt")
        : t("event_card.icons.offline_alt"),
    },
    {
      id: "host-kind",
      src: isBusinessHostEvent(ev)
        ? "/assets/events/organization_icon.svg"
        : "/assets/events/personal_icon.svg",
      alt: isBusinessHostEvent(ev)
        ? t("event_card.icons.organization_alt")
        : t("event_card.icons.personal_alt"),
    },
    ...(isPaid
      ? [
          {
            id: "paid",
            src: "/assets/events/payable_icon.svg",
            alt: t("event_card.icons.paid_alt"),
          },
        ]
      : []),
    ...(ev?.is_verified
      ? [
          {
            id: "verified",
            src: "/assets/events/verified_icon.svg",
            alt: t("event_card.icons.verified_alt"),
          },
        ]
      : []),
    ...(ev?.join_by_request ||
    (Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0)
      ? [
          {
            id: "private",
            src: "/assets/events/private_event.svg",
            alt: t("event_card.icons.by_request_alt"),
          },
        ]
      : []),
  ];

  return (
    <article className="relative min-h-[88px] w-full rounded-[18px] border border-white/22 bg-[linear-gradient(180deg,rgba(255,255,255,0.07)_0%,rgba(255,255,255,0.02)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
      <img
        src={imageSrc}
        alt={t("event_card.thumbnails.avatar_alt", { title: ev?.name || "" })}
        onError={(event) => {
          event.currentTarget.src = DEFAULT_EVENT_PICTURE_SRC;
        }}
        className="absolute left-0 top-0 h-[88px] w-[88px] rounded-[18px] border border-white object-cover"
      />
      <h3 className="absolute left-[104px] right-[52px] top-[14px] truncate text-[22px] font-semibold leading-[26px] text-white">
        {ev?.name}
      </h3>
      <button
        type="button"
        onClick={handleFavouriteClick}
        className="absolute right-[17px] top-[16px] flex h-[22px] w-[22px] items-center justify-center"
        aria-label={t("event_card.actions.favourite_aria")}
      >
        <img
          src={`/assets/events/${isFavourite ? "like_icon_on.svg" : "like_icon_off.svg"}`}
          alt=""
          aria-hidden="true"
          className="h-[21px] w-[21px]"
        />
      </button>
      <div className="absolute left-[104px] top-[52px] flex h-[24px] items-center gap-[8px]">
        {summaryMetaIcons.map((icon) => (
          <EventMetaIcon
            key={icon.id}
            src={icon.src}
            alt={icon.alt}
            className="h-[23px] w-[23px]"
          />
        ))}
      </div>
      {dateLabel ? (
        <div data-newcomer-tour="date" className="absolute right-[17px] top-[53px] max-w-[170px] truncate text-right text-[15px] font-semibold leading-[22px] text-white/58">
          {dateLabel}
        </div>
      ) : null}
    </article>
  );
};

const DesktopInfoTile = ({ label, children, onClick }) => {
  const content = (
    <>
      <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-white/45">
        {label}
      </span>
      <span className="mt-1 min-h-[22px] text-[15px] font-semibold leading-[21px] text-white/88">
        {children}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-[76px] flex-col rounded-[16px] border border-white/10 bg-white/[0.055] px-4 py-3 text-left transition hover:bg-white/[0.075]"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex min-h-[76px] flex-col rounded-[16px] border border-white/10 bg-white/[0.055] px-4 py-3">
      {content}
    </div>
  );
};

/**
 * Desktop-only content for EventDetailsFullscreen.
 *
 * ВАЖНО: эта разметка — прямое копирование десктоп-ветки из EventDetailsFullscreen
 * без изменения условий / классов / текстов. Всё поведение определяется пропсами.
 */
const EventDetailsDesktopContent = ({
  // i18n
  t,
  language,

  // event data
  ev,
  isConcept,
  cardDatetime,
  canAddToCalendar,
  onAddToCalendar = () => {},

  // user state
  isParticipant,
  isHost,
  hasConfirmedAttendance,

  // favourite
  favouriteState,
  toggleFav,

  // sharing / navigation
  linkCopied,
  shareEvent,
  goToMap,
  goToVault,
  onOpenParticipant,
  onOpenHost = () => {},
  openEventChat = () => {},
  joiningChat = false,
  openMainHostDirectChat = () => {},
  participantTicketing = null,
  ticketCancellationQuote = null,
  ticketCancellationLoading = false,
  ticketCancellationSubmitting = false,
  ticketDisputeSubmitting = false,
  onOpenTicketPayment = () => {},
  onLoadTicketCancellation = () => {},
  onSubmitTicketCancellation = () => {},
  onSubmitTicketDispute = () => {},
  onOpenTicketPanel = () => {},
  ticketPanelOpenRequest = 0,
  ticketPanelOpenView = "summary",
  onTicketPanelOpenHandled = () => {},

  // hosts
  hostList,

  // participants / reserve
  participantsSectionRef,
  loadingCode,
  userStorageCode,
  intentStatus,
  activationDateBadgesEnabled = false,
  reserveUI,
  isFull,
  unlockBeforeLabel,

  // payment / actions
  showPayButton,
  showIntentButton,
  showAttendanceButton,
  primaryActionType,
  newcomerInterestAction = null,
  confirmCtaLabel,
  prepayWindowT3d,
  ticketPaid,
  joinDisabledByReserve,
  unlockCountdownMs,
  unlockCountdownText,
  unlockText,
  isGuest,
  waitlistCtaVisible = false,
  waitlistCtaLabel = "",
  isInWaitlist = false,
  waitlistUnreadCount = 0,
  moreActionsUnreadCount = 0,
  participantRequestsPendingCount = 0,
  eventToolsOpenRequest = 0,
  eventToolsCloseRequest = 0,

  // handlers
  handleEditEvent,
  handleActivateConcept,
  handleHostConfirmEvent = () => {},
  handleReportEvent,
  handleRepeatEvent,
  handleJoinEvent,
  onJoinPaymentRequired = null,
  onCheckoutCapacityReserved = null,
  handleOpenWaitlist = () => {},
  handleOpenTicketSales = () => {},
  onMoreActionClick = null,
  onPrepareMoreAction = null,
  renderMoreActionView = null,
  hostConfirmationStatus = "pending",
  hostConfirmationRequestedAt = null,

  // state setters (modals / payment)
  setIsConfirmOpen,
  setIsLeaveConfirmOpen,
  onOpenLeaveConfirm = null,
  setIsIntentOpen,
  setIntentVariant,
  setPreJoinPaymentFlag,
  setTicketMode,
  setIsJoinConfirmOpen,
}) => {
  const navigate = useNavigate();
  const desktopContainerRef = React.useRef(null);
  const canJoinPartySession = canJoinEventSession({
    event: ev,
    isHost,
    isParticipant,
    hasConfirmedAttendance,
  });
  const isParticipantOnly = isParticipantOnlyViewer({ isHost, isParticipant });
  const openPartySession = (e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    navigate(`/events/${ev.id}/party-session`);
  };
  const hostConfirmationRequested = Boolean(hostConfirmationRequestedAt);
  const paidTicketTotalAmount = Number.parseFloat(
    participantTicketing?.total_paid_amount || "0"
  );
  const leaveRequiresPaidCancellation = Boolean(
    participantTicketing &&
    Number.isFinite(paidTicketTotalAmount) &&
    paidTicketTotalAmount > 0
  );
  const hasParticipantReservation = hasPaidParticipantReservation(participantTicketing);
  const hasTicketCancellationCase = Boolean(
    participantTicketing?.open_cancellation_request?.id ||
    participantTicketing?.latest_cancellation_request?.id
  );
  const leaveActionLabel = leaveRequiresPaidCancellation
    ? t(
        hasTicketCancellationCase
          ? "events.details.ticket_panel.view_cancellation"
          : "events.details.ticket_panel.cancel_reservation_button"
      )
    : t(isConcept ? "events.details.leave_concept" : "events.details.leave_event");
  const canMessageMainHost =
    !isHost && !isParticipant && !!String(ev?.host?.code || "").trim();
  const isAudienceLimitedForViewer = Boolean(
    !isGuest &&
    !isHost &&
    !isParticipant &&
    ev?.social_preference_fit?.is_within_audience === false
  );
  const limitedAudienceNotice = isAudienceLimitedForViewer ? (
    <div className="rounded-2xl border border-white/20 bg-white/12 px-5 py-4 text-center shadow-[0_12px_28px_rgba(5,10,28,0.18)] backdrop-blur-sm">
      <div className="text-sm font-extrabold uppercase tracking-[0.12em] text-white/90">
        {t("events.details.limited_audience.title", { defaultValue: "Limited audience" })}
      </div>
      <p className="mt-2 text-sm font-medium leading-5 text-white/74">
        {t("events.details.limited_audience.body", {
          defaultValue:
            "This event has a limited audience selected by the host. You can explore other events available for you.",
        })}
      </p>
      <button
        type="button"
        className="mt-3 h-11 rounded-2xl bg-white px-5 text-sm font-bold text-blue-600"
        onClick={(event) => {
          event.stopPropagation();
          navigate("/events/search");
        }}
      >
        {t("events.details.limited_audience.explore", {
          defaultValue: "Explore available events",
        })}
      </button>
    </div>
  ) : null;
  const [showMoreActions, setShowMoreActions] = React.useState(false);
  const [frozenDesktopHeight, setFrozenDesktopHeight] = React.useState(null);
  const lastEventToolsOpenRequestRef = React.useRef(eventToolsOpenRequest);
  const lastEventToolsCloseRequestRef = React.useRef(eventToolsCloseRequest);
  const getHostAvatarSrc = React.useCallback(
    (host) => resolveUserAvatarSrc(host, "/assets/events/default_avatar.png"),
    []
  );
  const getHostLabel = React.useCallback(
    (host) => host?.label || host?.name || host?.code || "",
    []
  );

  React.useEffect(() => {
    setShowMoreActions(false);
    setFrozenDesktopHeight(null);
  }, [ev?.id]);

  const showEventTools = React.useCallback(() => {
    const h = desktopContainerRef.current?.getBoundingClientRect?.().height;
    if (Number.isFinite(h) && h > 0) {
      setFrozenDesktopHeight(Math.ceil(h));
    }
    setShowMoreActions(true);
  }, []);

  const openMoreActions = React.useCallback((e) => {
    e?.stopPropagation?.();
    showEventTools();
  }, [showEventTools]);

  const closeMoreActions = React.useCallback((e) => {
    e?.stopPropagation?.();
    setShowMoreActions(false);
  }, []);

  React.useEffect(() => {
    if (lastEventToolsOpenRequestRef.current === eventToolsOpenRequest) return;
    lastEventToolsOpenRequestRef.current = eventToolsOpenRequest;
    if (!eventToolsOpenRequest) return;
    showEventTools();
  }, [eventToolsOpenRequest, showEventTools]);

  React.useEffect(() => {
    if (lastEventToolsCloseRequestRef.current === eventToolsCloseRequest) return;
    lastEventToolsCloseRequestRef.current = eventToolsCloseRequest;
    if (!eventToolsCloseRequest) return;
    setShowMoreActions(false);
  }, [eventToolsCloseRequest]);

  React.useEffect(() => {
    if (!showMoreActions) {
      setFrozenDesktopHeight(null);
    }
  }, [showMoreActions]);

  const renderUnreadBadge = React.useCallback((count) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    const label = n > 999 ? "999+" : String(n);
    return (
      <span
        className="pointer-events-none absolute right-4 top-1.5 z-20 bg-white text-blue-600 font-extrabold leading-none h-[18px] min-w-[18px] px-[5px] rounded-full ring-2 ring-blue-500 text-[11px] flex items-center justify-center shadow-[0_0_8px_rgba(255,255,255,0.45)]"
        aria-hidden="true"
      >
        {label}
      </span>
    );
  }, []);
  const renderHostMessageButton = React.useCallback(() => {
    if (!canMessageMainHost) return null;
    return (
      <button
        type="button"
        onClick={openMainHostDirectChat}
        className="h-12 w-12 shrink-0 rounded-2xl bg-white text-blue-600 flex items-center justify-center"
        aria-label={t("join_section.message_host_aria")}
        title={t("join_section.message_host_button")}
      >
        <img
          src="/assets/events/chat_with_host.svg"
          alt=""
          aria-hidden="true"
          className="w-6 h-6"
        />
      </button>
    );
  }, [canMessageMainHost, openMainHostDirectChat, t]);
  const renderPrimaryWithHostButton = React.useCallback(
    (primaryNode) => {
      const hostButton = renderHostMessageButton();
      if (!hostButton) return primaryNode;
      return (
        <div className="flex gap-2">
          <div className="flex-1 min-w-0">{primaryNode}</div>
          {hostButton}
        </div>
      );
    },
    [renderHostMessageButton]
  );
  const renderEventChatButton = React.useCallback(
    (className = "w-full h-12 rounded-2xl") => (
      <button
        type="button"
        onClick={openEventChat}
        disabled={joiningChat}
        className={`${className} font-semibold text-lg text-blue-600 bg-white mt-0 flex gap-2 justify-center items-center px-3 ${
          joiningChat ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        <img
          src={EVENT_CHAT_BUTTON_ICON_SRC}
          alt=""
          aria-hidden="true"
          className="w-[18px] h-[18px] shrink-0"
        />
        <span>{t(isConcept ? "events.details.concept_chat" : "events.details.event_chat")}</span>
      </button>
    ),
    [isConcept, joiningChat, openEventChat, t]
  );

  if (!ev) {
    return null;
  }
    const isHostlessConcept = Boolean(ev?.is_hostless_concept);
    const eventBoosted = Boolean(ev?.is_promoted_active ?? ev?.promoted);
    const eventStatus = String(ev?.status || "").toLowerCase();
    const isExpiredUnvalidated = eventStatus === "expired_unvalidated";
    const isCancelled = eventStatus === "cancelled";
    const isValidationPending = Boolean(
      isHost &&
      ev?.is_validated === false &&
      !["cancelled", "deleted", "finished", "expired_unvalidated"].includes(eventStatus)
    );
    const showHostTicketSales =
      isHost &&
      eventStatus !== "deleted" &&
      !isCancelled &&
      !isExpiredUnvalidated &&
      String(ev?.pricing_model || "").toLowerCase() === "host_paid" &&
      String(ev?.host_payment_collection_method || "").toLowerCase() === "card";
  const hostedByLabel = hostList.length > 1
      ? t("events.details.hosted_by_label_plural")
      : t("events.details.hosted_by_label_singular");
  const collaborationClub = ["club", "platform"].includes(hostList[0]?.type) ? hostList[0] : null;
  const collaborationUsers = collaborationClub
    ? hostList.filter((host) => !["club", "platform"].includes(host?.type))
    : [];

  const numericFeeType = Number(ev?.fee_type ?? 0);
  const isTicketPresentation = isEventTicketPresentation(ev);
  const feeDetails = [];
  if (Array.isArray(ev?.ticket_categories) && ev.ticket_categories.length) {
    ev.ticket_categories.forEach((item) => {
      if (String(item?.name || "").trim() && Number(item?.price) > 0) {
        feeDetails.push(`${String(item.name).trim()}: ${formatEventMoney(item.price, ev)}`);
      }
    });
  } else if (ev?.multisport_pricing_enabled) {
    if (Number(ev?.payment_category_1) > 0) {
      feeDetails.push(t("events.details.fee_without_multisport", { value: formatEventMoney(ev.payment_category_1, ev) }));
    }
    if (Number(ev?.multisport_ticket_price) > 0) {
      feeDetails.push(t("events.details.fee_with_multisport", { value: formatEventMoney(ev.multisport_ticket_price, ev) }));
    }
  } else if (ev?.gender_pricing_enabled) {
    if (Number(ev?.male_ticket_price) > 0) {
      feeDetails.push(t("events.details.fee_men_ticket", { value: formatEventMoney(ev.male_ticket_price, ev) }));
    }
    if (Number(ev?.female_ticket_price) > 0) {
      feeDetails.push(t("events.details.fee_women_ticket", { value: formatEventMoney(ev.female_ticket_price, ev) }));
    }
  } else if (Number(ev?.payment_category_1) > 0) {
    feeDetails.push(
      isTicketPresentation
        ? t("events.details.fee_ticket", { value: formatEventMoney(ev.payment_category_1, ev) })
        : t("event_description_modal.chips.shared_cost_with_amount", {
            amount: formatEventMoney(ev.payment_category_1, ev),
            defaultValue: "Shared Cost: {{amount}}",
          })
    );
  }
  if (Number(ev?.payment_category_2) > 0) {
    feeDetails.push(t("events.details.fee_rent", { value: formatEventMoney(ev.payment_category_2, ev) }));
  }
  if (Number(ev?.payment_category_3) > 0) {
    feeDetails.push(t("events.details.fee_provision", { value: formatEventMoney(ev.payment_category_3, ev) }));
  }
  if (Number(ev?.payment_category_4) > 0) {
    feeDetails.push(t("events.details.fee_other", { value: formatEventMoney(ev.payment_category_4, ev) }));
  }
  const eventCoverSrc = getEventCoverSrc(ev);
  const eventIconSrc = String(ev?.icon_image_url || "").trim();
  const summaryDateLabel = isConcept
    ? t("event_card.concept_label", { defaultValue: "Concept" })
    : formatEventCardDateTimeLabel(ev, cardDatetime, language);
  const addressOrLinkLabel =
    ev.event_format === 0
      ? t("events.details.link_label")
      : t("events.details.address_label");
  const addressOrLinkValue =
    ev.exact_venue_hidden
      ? t("join_section.singles_party.venue_hidden")
      : ev.event_format === 0
      ? (isParticipant || isHost
          ? t("events.details.link_to_event")
          : t("events.details.join_to_get_link"))
      : formatPhysicalEventLocation(ev, t("events.details.show_on_map"), language);
  const categoryLabel = ev?.category
    ? ev.category.charAt(0).toUpperCase() + ev.category.slice(1)
    : "";
  const feeLabel =
    numericFeeType === 0
      ? t(isConcept ? "events.details.fee_free_concept" : "events.details.fee_free")
      : (feeDetails.length > 0 ? feeDetails.join(" / ") : t("events.details.fee_type_label"));
  const awaitingConceptDetails = isConceptAwaitingDetails(ev);

  return (
    <div
      ref={desktopContainerRef}
      className="flex min-h-0 flex-col gap-4 text-white"
      style={
        showMoreActions && frozenDesktopHeight != null
          ? { minHeight: `${frozenDesktopHeight}px` }
          : undefined
      }
    >
      <div className="relative overflow-hidden rounded-[24px] border border-white/12 bg-[linear-gradient(180deg,rgba(14,31,55,0.98)_0%,rgba(7,16,31,0.99)_100%)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_18px_44px_rgba(0,0,0,0.26)]">
        {eventCoverSrc ? (
          <>
            <img
              src={eventCoverSrc}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-[170px] w-full object-cover opacity-28"
            />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[220px] bg-[linear-gradient(180deg,rgba(5,8,18,0.18)_0%,rgba(5,8,18,0.78)_72%,rgba(5,8,18,1)_100%)]" />
          </>
        ) : null}
        <div className="relative z-10">
          <DesktopEventSummaryCard
            ev={ev}
            imageSrc={getEventSummaryImageSrc({
              eventIconSrc,
              badgeLabel: ev?.badge_label,
            })}
            dateLabel={summaryDateLabel}
            isFavourite={favouriteState}
            onToggleFavourite={toggleFav}
            t={t}
          />


          {isValidationPending ? (
            <div className="mt-4">
              <ValidationPendingNotice
                title={t(
                  isConcept
                    ? "events.details.validation_pending.title_concept"
                    : "events.details.validation_pending.title_event"
                )}
                body={t(
                  isConcept
                    ? "events.details.validation_pending.body_concept"
                    : "events.details.validation_pending.body_event"
                )}
              />
            </div>
          ) : null}

          {isConcept ? (
            <div className="mt-3 rounded-full bg-white/10 px-3 py-1 text-center text-[13px] font-semibold text-white/78">
              {t("events.details.concept_datetime_note")}
            </div>
          ) : null}

          <div className="mt-4 rounded-[18px] border border-white/10 bg-white/[0.04] px-1 py-3">
            <EventMetaSection
              ev={ev}
              isParticipant={isParticipant}
              isHost={isHost}
              linkCopied={linkCopied}
              shareEvent={shareEvent}
              goToMap={goToMap}
              goToVault={goToVault}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
            {!awaitingConceptDetails && <DesktopInfoTile
              label={addressOrLinkLabel}
              onClick={ev.event_format === 0 || ev.exact_venue_hidden ? undefined : goToMap}
            >
              {addressOrLinkValue}
            </DesktopInfoTile>}
            <DesktopInfoTile label={t("events.details.category_label")}>
              {categoryLabel}
            </DesktopInfoTile>
            <DesktopInfoTile label={t("events.details.status_label")}>
              {isValidationPending
                ? t("events.details.validation_pending.status")
                : isExpiredUnvalidated
                ? t("events.details.expired_unvalidated_status")
                : isCancelled
                  ? t("events.details.cancelled_status")
                : ev.status}
            </DesktopInfoTile>
            {!(awaitingConceptDetails && numericFeeType === 0) && (
              <DesktopInfoTile label={t("events.details.fee_type_label")}>
                {feeLabel}
              </DesktopInfoTile>
            )}
          </div>

          {hostList.length > 0 ? (
            <div data-newcomer-tour="hosts" className="mt-4 rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-3">
              <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/45">
                {hostedByLabel}
              </div>
              {collaborationClub && collaborationUsers.length ? (
                <div className="flex flex-col items-start">
                  <button
                    type="button"
                    onClick={() => onOpenHost(collaborationClub)}
                    disabled={collaborationClub.type === "platform"}
                    className="flex max-w-full items-center gap-2 text-left transition hover:opacity-90"
                  >
                    <img
                      src={getHostAvatarSrc(collaborationClub)}
                      alt="host avatar"
                      className="h-[42px] w-[42px] shrink-0 rounded-full object-cover ring-2 ring-white/70"
                    />
                    <span className="max-w-[240px] break-words text-sm font-extrabold leading-5 text-white">
                      {getHostLabel(collaborationClub)}
                    </span>
                  </button>
                  <div aria-hidden="true" className="flex h-7 w-[42px] items-center justify-center text-lg font-extrabold leading-none text-white">
                    ×
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {collaborationUsers.map((host) => (
                      <button
                        key={host.id || host.code || host.label}
                        type="button"
                        onClick={() => onOpenHost(host)}
                        className="flex w-[42px] flex-col items-center transition hover:opacity-90"
                      >
                        <img
                          src={getHostAvatarSrc(host)}
                          alt="host avatar"
                          className={`h-[42px] w-[42px] rounded-full object-cover ${
                            host?.avatar_custom_url ? "ring-2 ring-white/70" : ""
                          }`}
                        />
                        <PremiumCodeLabel
                          as="span"
                          code={getHostLabel(host)}
                          isPremium={Boolean(host?.has_premium_subscription)}
                          className="mt-1 max-w-[72px] truncate text-sm font-extrabold text-white"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {hostList.map((host) => (
                    <button
                      key={host.id || host.code || host.label}
                      type="button"
                      onClick={() => onOpenHost(host)}
                      className="flex min-w-[112px] items-center gap-2 rounded-full border border-white/10 bg-white/[0.055] px-2.5 py-2 text-left transition enabled:hover:bg-white/[0.075]"
                    >
                      <img
                        src={getHostAvatarSrc(host)}
                        alt="host avatar"
                        className={`h-[42px] w-[42px] rounded-full object-cover ${
                          host?.avatar_custom_url || host?.imageUrl ? "ring-2 ring-white/70" : ""
                        }`}
                      />
                      <PremiumCodeLabel
                        as="span"
                        code={getHostLabel(host)}
                        isPremium={Boolean(
                          host?.type !== "club" && host?.has_premium_subscription
                        )}
                        className="max-w-full break-words text-sm font-extrabold text-white"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div>
        <div
          className="px-1 pb-2"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <HostMoreActionsPanel
          paymentRequest={usesHostPaymentRequest(ev)}
            open={showMoreActions}
            t={t}
            isPrivateEvent={Boolean(
              ev?.join_by_request ||
                Boolean(ev?.skill_access?.enabled) ||
                Number(ev?.participant_requests_pending_total || 0) > 0 ||
                (Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0)
            )}
            isConcept={Boolean(isConcept)}
            hasConceptDatePoll={Boolean(ev?.concept_date_poll?.exists)}
            waitlistUnreadCount={waitlistUnreadCount}
            participantRequestsPendingCount={participantRequestsPendingCount}
            onBack={closeMoreActions}
            onActionClick={onMoreActionClick}
            onPrepareAction={onPrepareMoreAction}
            renderActionView={renderMoreActionView}
            showLifecycleActions={!isHostlessConcept}
            eventBoosted={eventBoosted}
            hostTransferUnavailable={isPaidEventHostTransferUnavailable(ev)}
          />

          {!showMoreActions && (
            <TranslatableEventDescription
              eventId={ev?.id}
              title={t(
                isConcept
                  ? "events.details.about_concept"
                  : "events.details.about_event"
              )}
              description={ev?.description}
              wrapperClassName="mb-4"
              titleClassName="text-white text-xl font-semibold"
              descriptionClassName="whitespace-pre-line text-white break-words [overflow-wrap:anywhere]"
            />
          )}

        </div>
      </div>

      {!showMoreActions && isHostlessConcept && (
        <HostRewardBlock
          reward={ev?.host_claim_reward || null}
          align="center"
          className="mb-4"
          titleClassName="text-xl font-bold text-white"
          showHintToggle={true}
        />
      )}

      {/* Participants */}
      {!showMoreActions && (
        <>
        <div data-newcomer-tour="gallery">
          <EventPromoGallerySection
            items={Array.isArray(ev?.promo_gallery) ? ev.promo_gallery : []}
            isHost={isHost}
            onEdit={() => handleEditEvent("promo_gallery")}
          />
        </div>
        <div ref={participantsSectionRef} data-newcomer-tour="participants">
          {ev?.is_singles_party && !isHost && !isParticipant ? (
            <SinglesPartyAttendanceSummary event={ev} />
          ) : (
            <ParticipantsSection
              variant="desktop"
              participants={ev.participants}
              participantsCount={ev?.participants_count ?? ev?.participants?.length ?? 0}
              onOpenParticipant={onOpenParticipant}
              loadingCode={loadingCode}
              userStorageCode={userStorageCode}
              intentStatus={intentStatus}
              activationDateBadgesEnabled={activationDateBadgesEnabled}
              showPaymentBadges={!!ev?.host_payment_badges_enabled}
              isHost={isHost}
              reserveUI={{ ...reserveUI, max_participants: ev.max_participants }}
              isFull={isFull}
              unlockBeforeLabel={unlockBeforeLabel}
            />
          )}
        </div>
        </>
      )}

      {!showMoreActions && (
        <EventBuddyPanel
          eventId={ev?.id}
          buddyFeature={ev?.buddy_feature}
          isParticipant={isParticipant || isHost}
          onOpenVault={goToVault}
        />
      )}

      {!showMoreActions &&
      ev.status !== "deleted" &&
      ev.status !== "finished" &&
      hasConfirmedAttendance &&
      isParticipant ? (
        <div className="mb-2 flex min-h-11 items-center justify-center rounded-2xl border border-emerald-300/25 bg-emerald-400/15 px-4 text-center text-sm font-bold text-emerald-50">
          {t("events.details.attendance_confirmed")}
        </div>
      ) : null}

        {!showMoreActions && showHostTicketSales ? (
          <button
            type="button"
            onClick={handleOpenTicketSales}
            className="mb-2 flex h-12 w-full items-center justify-center rounded-2xl bg-white text-lg font-semibold text-blue-600"
          >
            {t("events.details.ticket_sales", { defaultValue: "Ticket sales" })}
          </button>
        ) : null}

      {!showMoreActions && isHost && isParticipant && showAttendanceButton &&
        !hasConfirmedAttendance && ev.status !== "deleted" && ev.status !== "finished" && (
        <button
          type="button"
          className="mb-2 h-12 w-full rounded-2xl bg-white text-lg font-semibold text-blue-600"
          onClick={() => setIsConfirmOpen(true)}
        >
          {t("events.details.confirm_attendance")}
        </button>
      )}

      {/* Pending — participant */}
      {!showMoreActions && ev.status === "pending" && isParticipantOnly && (
        !hasConfirmedAttendance ? (
          <div className="space-y-2">
            <div>
              {showAttendanceButton && (
                <button
                  onClick={() => setIsConfirmOpen(true)}
                  className="h-12 w-full rounded-2xl bg-white text-lg font-semibold text-blue-600"
                >
                  {t("events.details.confirm_attendance")}
                </button>
              )}
            </div>
            {showPayButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const v = "payment";
                  setIntentVariant(v);
                  setPreJoinPaymentFlag(false); // participant paying for an already reserved spot
                  setTicketMode(true); // "Get Your Ticket" copy in pending state
                  setIsIntentOpen(true);
                }}
                className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
              >
                {t("events.details.pay_for_ticket")}
              </button>
            )}
            {hasParticipantReservation ? (
              <ParticipantTicketPanel
                t={t}
                ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                cancellationQuote={ticketCancellationQuote}
                cancellationLoading={ticketCancellationLoading}
                cancellationSubmitting={ticketCancellationSubmitting}
                disputeSubmitting={ticketDisputeSubmitting}
                onPayNow={onOpenTicketPayment}
                onLoadCancellation={onLoadTicketCancellation}
                onSubmitCancellation={onSubmitTicketCancellation}
                onSubmitDispute={onSubmitTicketDispute}
                triggerClassName="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
                externalOpenToken={ticketPanelOpenRequest}
                externalOpenView={ticketPanelOpenView}
                onExternalOpenHandled={onTicketPanelOpenHandled}
              />
            ) : null}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {renderEventChatButton("h-12 rounded-2xl")}
              {canJoinPartySession ? (
                <button
                  onClick={openPartySession}
                  className="h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
                >
                  {t("events.details.join_session")}
                </button>
              ) : null}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasParticipantReservation) {
                    onOpenTicketPanel("summary");
                  } else if (typeof onOpenLeaveConfirm === "function") {
                    onOpenLeaveConfirm(e);
                  } else {
                    setIsLeaveConfirmOpen(true);
                  }
                }}
                className="col-span-2 h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
              >
                {hasParticipantReservation
                  ? t("events.details.ticket_panel.cta_label")
                  : leaveActionLabel}
              </button>
            </div>
            {hasParticipantReservation ? (
              <ParticipantTicketPanel
                t={t}
                ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                cancellationQuote={ticketCancellationQuote}
                cancellationLoading={ticketCancellationLoading}
                cancellationSubmitting={ticketCancellationSubmitting}
                disputeSubmitting={ticketDisputeSubmitting}
                onPayNow={onOpenTicketPayment}
                onLoadCancellation={onLoadTicketCancellation}
                onSubmitCancellation={onSubmitTicketCancellation}
                onSubmitDispute={onSubmitTicketDispute}
                hideTrigger
                externalOpenToken={ticketPanelOpenRequest}
                externalOpenView={ticketPanelOpenView}
                onExternalOpenHandled={onTicketPanelOpenHandled}
              />
            ) : null}
          </>
        )
      )}

      {/* Finished — participant */}
      {!showMoreActions && ev.status === "finished" && (isParticipant || isHost) && (
        isHost ? (
          <button
            onClick={handleRepeatEvent}
            className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
          >
            {t("events.details.repeat_event")}
          </button>
        ) : (
          <div className="space-y-2">
            {hasParticipantReservation ? (
              <ParticipantTicketPanel
                t={t}
                ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                cancellationQuote={ticketCancellationQuote}
                cancellationLoading={ticketCancellationLoading}
                cancellationSubmitting={ticketCancellationSubmitting}
                disputeSubmitting={ticketDisputeSubmitting}
                onPayNow={onOpenTicketPayment}
                onLoadCancellation={onLoadTicketCancellation}
                onSubmitCancellation={onSubmitTicketCancellation}
                onSubmitDispute={onSubmitTicketDispute}
                triggerClassName="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
                externalOpenToken={ticketPanelOpenRequest}
                externalOpenView={ticketPanelOpenView}
                onExternalOpenHandled={onTicketPanelOpenHandled}
              />
            ) : null}
          </div>
        )
      )}

      {!showMoreActions && isExpiredUnvalidated && isHost && (
        <div className="space-y-3 rounded-[18px] border border-white/14 bg-white/[0.055] p-4">
          <p className="text-center text-sm font-medium leading-5 text-white/75">
            {t("events.details.expired_unvalidated_note")}
          </p>
          <button
            type="button"
            onClick={handleRepeatEvent}
            className="h-12 w-full rounded-2xl bg-white text-lg font-semibold text-blue-600"
          >
            {t("events.details.repeat_event")}
          </button>
        </div>
      )}
      {!showMoreActions && ev.status === "pending" && isConcept && isHost && !canJoinPartySession && (
        <button
          type="button"
          className="w-full h-12 rounded-2xl bg-white text-lg font-semibold text-blue-600"
          onClick={(e) => {
            e.stopPropagation();
            handleEditEvent();
          }}
        >
          {t("events.details.edit_concept")}
        </button>
      )}

      {/* Main CTA row (non-pending / non-finished / non-deleted) */}
      {!showMoreActions && newcomerInterestAction ? (
        <button
          type="button"
          className="mb-2 flex h-12 w-full items-center justify-center rounded-2xl bg-white px-5 text-lg font-semibold text-blue-600 shadow-[0_12px_28px_rgba(0,0,0,0.22)] transition hover:bg-white/90"
          onClick={newcomerInterestAction.onClick}
        >
          <ChatNotificationBellIcon size={24} className="mr-2 shrink-0" />
          <span>{newcomerInterestAction.label}</span>
        </button>
      ) : null}
      {!showMoreActions &&
        ev.status !== "deleted" &&
        ev.status !== "finished" &&
        (ev.status !== "pending" || (isHost && canJoinPartySession)) &&
        !isExpiredUnvalidated &&
        (isHost || !hasConfirmedAttendance) && (
          <div>
            {isHost ? (
              isConcept ? (
                <div className="space-y-2">
                  <button
                    className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivateConcept();
                    }}
                  >
                    {t("events.details.activate_concept")}
                  </button>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent();
                      }}
                    >
                      {t("events.details.edit_concept")}
                    </button>
                    <button
                      className="relative flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0 flex gap-2 justify-center items-center px-3"
                      onClick={openMoreActions}
                    >
                      {renderUnreadBadge(moreActionsUnreadCount)}
                      <img
                        src="/assets/events/more_actions_icon.svg"
                        alt="Event tools icon"
                        className="w-4 h-4"
                      />
                      <span>{t("events.details.more_actions.open_button")}</span>
                    </button>
                  </div>
                  {renderEventChatButton()}
                </div>
              ) : (
                hostConfirmationStatus !== "confirmed" && hostConfirmationRequested ? (
                  <div className="space-y-2">
                    <button
                      className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHostConfirmEvent(e);
                      }}
                    >
                      {t("events.details.confirm_hosting")}
                    </button>
                    <div className="flex gap-2">
                      <button
                        className="flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0 flex gap-2 justify-center items-center px-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent();
                        }}
                      >
                        <img
                          src="/assets/events/edit_event.svg"
                          alt="Edit event icon"
                          className="w-[18px] h-[18px] shrink-0"
                        />
                        <span>{t("events.details.edit_event")}</span>
                      </button>
                      <button
                        className="relative flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0 flex gap-2 justify-center items-center px-3"
                        onClick={openMoreActions}
                      >
                        {renderUnreadBadge(moreActionsUnreadCount)}
                        <img
                          src="/assets/events/more_actions_icon.svg"
                          alt="Event tools icon"
                          className="w-4 h-4"
                        />
                        <span>{t("events.details.more_actions.open_button")}</span>
                      </button>
                    </div>
                    {canJoinPartySession ? (
                      <div className="grid grid-cols-2 gap-2">
                        {renderEventChatButton("h-12 rounded-2xl")}
                        <button
                          type="button"
                          onClick={openPartySession}
                          className="h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
                        >
                          {t("events.details.join_session")}
                        </button>
                      </div>
                    ) : renderEventChatButton()}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        className="flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0 flex gap-2 justify-center items-center px-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent();
                        }}
                      >
                        <img
                          src="/assets/events/edit_event.svg"
                          alt="Edit event icon"
                          className="w-[18px] h-[18px] shrink-0"
                        />
                        <span>{t("events.details.edit_event")}</span>
                      </button>
                      <button
                        className="relative flex-1 h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl mt-0 flex gap-2 justify-center items-center px-3"
                        onClick={openMoreActions}
                      >
                        {renderUnreadBadge(moreActionsUnreadCount)}
                        <img
                          src="/assets/events/more_actions_icon.svg"
                          alt="Event tools icon"
                          className="w-4 h-4"
                        />
                        <span>{t("events.details.more_actions.open_button")}</span>
                      </button>
                    </div>
                    {canJoinPartySession ? (
                      <div className="grid grid-cols-2 gap-2">
                        {renderEventChatButton("h-12 rounded-2xl")}
                        <button
                          type="button"
                          onClick={openPartySession}
                          className="h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
                        >
                          {t("events.details.join_session")}
                        </button>
                      </div>
                    ) : renderEventChatButton()}
                  </div>
                )
              )
            ) : isParticipant ? (
              <div className="space-y-2">
                {showIntentButton ? (
                    <button
                      onClick={() => {
                        const v = primaryActionType === "activation_date"
                          ? "activation_date"
                          : primaryActionType === "slot"
                          ? "slot"
                          : (prepayWindowT3d && !ticketPaid ? "payment" : "intent");
                        setIntentVariant(v);
                        setPreJoinPaymentFlag(false); // participant paying to keep spot
                        setTicketMode(false); // classic "keep your spot" flow
                        setIsIntentOpen(true);
                      }}
                      className="w-full flex gap-2 justify-center items-center text-lg font-semibold text-blue-600 bg-white rounded-2xl h-12"
                    >
                      {confirmCtaLabel}
                    </button>
                  ) : (
                    showAttendanceButton && (
                      <button
                        onClick={() => setIsConfirmOpen(true)}
                        className="w-full flex gap-2 justify-center items-center text-lg font-semibold text-blue-600 bg-white rounded-2xl h-12"
                      >
                        {confirmCtaLabel}
                      </button>
                    )
                  )}
                <div className="flex space-x-2">
                  {renderEventChatButton("flex-1 h-12 rounded-2xl")}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasParticipantReservation) {
                        onOpenTicketPanel("summary");
                      } else if (typeof onOpenLeaveConfirm === "function") {
                        onOpenLeaveConfirm(e);
                      } else {
                        setIsLeaveConfirmOpen(true);
                      }
                    }}
                    className="flex-1 flex gap-2 justify-center items-center text-lg font-semibold text-blue-600 bg-white rounded-2xl h-12"
                  >
                      {hasParticipantReservation
                        ? t("events.details.ticket_panel.cta_label")
                        : leaveActionLabel}
                    </button>
                </div>
                {showPayButton && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const v = "payment";
                      setIntentVariant(v);
                      setPreJoinPaymentFlag(false); // participant paying for an already reserved spot
                      setTicketMode(true); // "Get Your Ticket" copy
                      setIsIntentOpen(true);
                    }}
                    className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
                  >
                    {t("events.details.pay_for_ticket")}
                  </button>
                )}
                {hasParticipantReservation ? (
                  <ParticipantTicketPanel
                    t={t}
                    ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                    cancellationQuote={ticketCancellationQuote}
                    cancellationLoading={ticketCancellationLoading}
                    cancellationSubmitting={ticketCancellationSubmitting}
                    disputeSubmitting={ticketDisputeSubmitting}
                    onPayNow={onOpenTicketPayment}
                    onLoadCancellation={onLoadTicketCancellation}
                    onSubmitCancellation={onSubmitTicketCancellation}
                    onSubmitDispute={onSubmitTicketDispute}
                    hideTrigger
                    externalOpenToken={ticketPanelOpenRequest}
                    externalOpenView={ticketPanelOpenView}
                    onExternalOpenHandled={onTicketPanelOpenHandled}
                  />
                ) : null}
                {canAddToCalendar && (
                  <button
                    onClick={(e) => {
                      onAddToCalendar(e);
                    }}
                    className="w-full h-12 font-semibold text-lg text-blue-600 bg-white rounded-2xl"
                  >
                    {t("events.details.join_chat.add_to_calendar")}
                  </button>
                )}
              </div>
            ) : isAudienceLimitedForViewer ? (
              limitedAudienceNotice
            ) : !isFull ? (
              <div className="space-y-2">
                {isInWaitlist ? (
                  renderPrimaryWithHostButton(
                    <button
                      className="w-full h-12 font-semibold text-lg rounded-2xl mt-0 bg-white text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenWaitlist(e);
                      }}
                    >
                      {waitlistCtaLabel || t("events.details.waitlist.view_waitlist")}
                    </button>
                  )
                ) : shouldUseParticipantRequestFlow(ev) ? (
                  isGuest ? (
                    renderPrimaryWithHostButton(
                      <button
                        className="w-full h-12 font-semibold text-lg rounded-2xl mt-0 bg-white text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinEvent(e);
                        }}
                      >
                        {t(
                          isHostlessConcept
                            ? "events.details.claim_host_cta"
                            : isConcept
                              ? "events.details.join_concept"
                              : "events.details.join_event"
                        )}
                      </button>
                    )
                  ) : (
                    <JoinSection
                      event={ev}
                      isParticipant={false}
                      api={api}
                      eventId={ev.id}
                      onJoined={() => {}}
                      onOpenChat={openEventChat}
                      onOpenAfterJoinModal={() => setIsJoinConfirmOpen?.(true)}
                      onPaymentRequired={onJoinPaymentRequired}
                      onCheckoutCapacityReserved={onCheckoutCapacityReserved}
                      secondaryAction={
                        canMessageMainHost
                          ? {
                              label: t("join_section.message_host_button"),
                              ariaLabel: t("join_section.message_host_aria"),
                              onClick: openMainHostDirectChat,
                              iconSrc: "/assets/events/chat_with_host.svg",
                            }
                          : null
                      }
                    />
                  )
                ) : (
                  !isGuest && !isInWaitlist && joinDisabledByReserve && waitlistCtaVisible ? (
                    renderPrimaryWithHostButton(
                      <button
                        className="w-full h-12 font-semibold text-lg rounded-2xl mt-0 bg-white text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenWaitlist(e);
                        }}
                      >
                        {waitlistCtaLabel || t("events.details.waitlist.join_waitlist")}
                      </button>
                    )
                  ) : (
                    renderPrimaryWithHostButton(
                      <button
                        className={`w-full h-12 font-semibold text-lg rounded-2xl mt-0 ${
                          joinDisabledByReserve && !isGuest
                            ? "bg-white/30 text-white/70 cursor-not-allowed"
                            : "bg-white text-blue-600"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();

                          // Guests should always be redirected to auth on Join
                          if (isGuest) {
                            handleJoinEvent(e);
                            return;
                          }

                          if (joinDisabledByReserve) {
                            window.dispatchEvent(
                              new CustomEvent("global-info", {
                                detail: {
                                  message:
                                    unlockCountdownText != null
                                      ? `Reserved for new joiners. Unlock in ${unlockCountdownText}`
                                      : unlockText
                                      ? `Reserved for new joiners. Unlocks at ${unlockText}`
                                      : "Reserved for new joiners",
                                },
                              })
                            );
                            return;
                          }

                          handleJoinEvent(e);
                        }}
                        disabled={joinDisabledByReserve && !isGuest}
                        title={
                          joinDisabledByReserve && !isGuest
                            ? unlockCountdownText != null
                              ? `Reservation release in ${unlockCountdownText}`
                              : unlockText
                              ? `Reserved • unlocks at ${unlockText}`
                              : "Reserved for new joiners"
                            : undefined
                        }
                        aria-disabled={
                          joinDisabledByReserve && !isGuest ? "true" : "false"
                        }
                      >
                        {joinDisabledByReserve && !isGuest
                          ? unlockCountdownText != null
                            ? `Reservation release in ${unlockCountdownText}`
                            : unlockText
                            ? `Reserved • unlock ${unlockText}`
                            : "Reserved for new joiners"
                          : t(
                              isHostlessConcept
                                ? "events.details.claim_host_cta"
                                : isConcept
                                  ? "events.details.join_concept"
                                  : "events.details.join_event"
                            )}
                      </button>
                    )
                  )
                )}
              </div>
            ) : shouldUseParticipantRequestFlow(ev) ? (
              isGuest ? (
                renderPrimaryWithHostButton(
                  <button
                    className="w-full h-12 font-semibold text-lg rounded-2xl mt-0 bg-white text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJoinEvent(e);
                    }}
                  >
                    {t(isConcept ? "events.details.join_concept" : "events.details.join_event")}
                  </button>
                )
              ) : (
                <JoinSection
                  event={ev}
                  isParticipant={false}
                  api={api}
                  eventId={ev.id}
                  onJoined={() => {}}
                  onOpenChat={openEventChat}
                  onOpenAfterJoinModal={() => setIsJoinConfirmOpen?.(true)}
                  onPaymentRequired={onJoinPaymentRequired}
                  onCheckoutCapacityReserved={onCheckoutCapacityReserved}
                  secondaryAction={
                    canMessageMainHost
                      ? {
                          label: t("join_section.message_host_button"),
                          ariaLabel: t("join_section.message_host_aria"),
                          onClick: openMainHostDirectChat,
                          iconSrc: "/assets/events/chat_with_host.svg",
                        }
                      : null
                  }
                />
              )
            ) : waitlistCtaVisible ? (
              renderPrimaryWithHostButton(
                <button
                  className="w-full h-12 font-semibold text-lg rounded-2xl mt-0 bg-white text-blue-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenWaitlist(e);
                  }}
                >
                  {waitlistCtaLabel || t("events.details.waitlist.join_waitlist")}
                </button>
              )
            ) : null}
          </div>
        )}

      {/* Confirmed participant actions; status is shown with the page content above. */}
      {!showMoreActions &&
        ev.status !== "deleted" &&
        ev.status !== "finished" &&
        ev.status !== "pending" &&
        hasConfirmedAttendance &&
        isParticipantOnly && (
          <div className="mt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {renderEventChatButton("h-12 rounded-2xl")}
              {canJoinPartySession ? (
                <button
                  onClick={openPartySession}
                  className="h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
                >
                  {t("events.details.join_session")}
                </button>
              ) : null}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasParticipantReservation) {
                    onOpenTicketPanel("summary");
                  } else if (typeof onOpenLeaveConfirm === "function") {
                    onOpenLeaveConfirm(e);
                  } else {
                    setIsLeaveConfirmOpen(true);
                  }
                }}
                className="col-span-2 h-12 rounded-2xl bg-white px-3 text-base font-semibold text-blue-600"
              >
                {hasParticipantReservation
                  ? t("events.details.ticket_panel.cta_label")
                  : leaveActionLabel}
              </button>
            </div>
            {hasParticipantReservation ? (
              <ParticipantTicketPanel
                t={t}
                ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                cancellationQuote={ticketCancellationQuote}
                cancellationLoading={ticketCancellationLoading}
                cancellationSubmitting={ticketCancellationSubmitting}
                disputeSubmitting={ticketDisputeSubmitting}
                onPayNow={onOpenTicketPayment}
                onLoadCancellation={onLoadTicketCancellation}
                onSubmitCancellation={onSubmitTicketCancellation}
                onSubmitDispute={onSubmitTicketDispute}
                hideTrigger
                externalOpenToken={ticketPanelOpenRequest}
                externalOpenView={ticketPanelOpenView}
                onExternalOpenHandled={onTicketPanelOpenHandled}
              />
            ) : null}
          </div>
        )}
    </div>
  );
};

export default EventDetailsDesktopContent;
